import { format } from 'date-fns'
import { ReactNode } from 'react'
import { AlertColor } from '@mui/material'
import { defaultLocale } from '../constants'
import { DiversityDimenstionConfig, ModuleConfig } from '../model'

export interface WizardStep {
  name: string
  position: number
  finished: boolean
}

export interface WizardConfiguration {
  steps: WizardStep[]
  activeStep: WizardStep
}

export interface CharacteristicConfig {
  id: number
  name: String
  scaleType: 'ORDINAL' | 'INTERVAL' | 'NOMINAL' | 'BINARY'
  operation: 'SQUARED' | 'GROUPED' | 'ORIGINAL'
  autoSelection: boolean
  originalCharacteristic: Characteristic | null | undefined
}

export interface EmpiricalConceptConfig {
  empiricalConcept: EmpiricalConcept
  mandatory: boolean
  feasibleCharacteristics: CharacteristicConfig[]
}

export interface ScaleType {
  id: number
  scaleType: string
}

export interface Module {
  id: number
  name: string
  enabled: boolean
  dependentEmpiricalConcept: EmpiricalConcept
  contextModels: ContextModel[]
  scaleTypes: ScaleType[]
}

export interface ContextModelSummary {
  fairnessAchieved: number
  dependentCharacteristic: Characteristic
  diversityDimensionCharacteristic: Characteristic
  contextModelVariants: ContextModelVariant[]
}

export interface ModuleSummary {
  moduleId: number
  moduleName: string
  fairnessAchieved: number
  previousRunFairness: number | null
  contextModelSummaries: ContextModelSummary[]
}

export interface ContextModel {
  id: number
  contextModelVariants: ContextModelVariant[]
  dependentCharacteristic: Characteristic
  diversityDimension: Characteristic
}

export interface ContextModelPost {
  dependentCharacteristicId: number
  diversityDimensionCharacteristicId: number
  moduleId: number
  dependentVariableId: number
}

export interface IndependentCharacteristicPost {
  characteristicId: number
  reference: string | null | undefined
}

export interface DescriptiveCharacteristicPost {
  characteristicId: number
}

export interface DiversityVariant {
  id: number
  diversityEmpiricalConcept: EmpiricalConcept
  diversityCharacteristic: Characteristic
  referenceGroupVariable: Variable
  groupOfInterestVariable: Variable
}

export interface DiversityVariantPostData {
  diversityEmpiricalConceptId: number
  diversityCharacteristicId: number
  referenceGroupVariableId: number
  groupOfInterestVariableId: number
}

export interface ContextModelVariant {
  id: number
  contextModelId: number
  diversityVariant: DiversityVariant
}

export interface Characteristic {
  id: number
  name: string
  scaleType: 'NOMINAL' | 'INTERVAL' | 'BINARY' | 'ORDINAL'
  empiricalConcept: EmpiricalConcept
  variables: Variable[]
}

export interface Gap {
  id: number
  contextModelVariantId: number
  dependentCharacteristic: Characteristic
  diversityDimensionCharacteristic: Characteristic
  referenceGroup: string
  groupOfInterest: string
  adjusted: number
  unadjusted: number
  unadjustedRegression: number
  timeOfImport: Date
}

export interface GapExtended extends Gap {
  gapResultDescriptions: Record<string, any>[]
  description: GapDescription
  causes: GapCause[]
}

export interface EmpiricalConcept {
  id: number
  name: string
  isDiversityDimension: Boolean
  order: number
}

export interface GapCause {
  empiricalConcept: EmpiricalConcept
  explainedPart: number
  unexplainedPart: number
}

export interface GapDescription {
  referenceGroup: DescriptionSubGroup
  groupOfInterest: DescriptionSubGroup
}

export interface DescriptionSubGroup {
  groupName: string
  groupVariable: Variable
  descriptionDependentVariable: Description
  observationsRegression: number
}

export interface Description {
  characteristic: Characteristic
  mean: number
  median: number
  standardDeviation: number
  min: number
  max: number
  numberOfObservations: number
}

export interface DetailedGapDescription {
  referenceGroup: DetailedDescriptionSubGroup
  groupOfInterest: DetailedDescriptionSubGroup
  gapCauseDescriptions: Record<string, any>[]
}

export interface DetailedDescriptionSubGroup {
  groupName: string
  groupVariable: Variable
  descriptiveEmpiricalConcept: EmpiricalConcept
  descriptiveCharacteristic: Characteristic
  descriptions: DetailedDescription[]
}

export interface DetailedDescription {
  descriptionDependentVariable: Description
  descriptiveName: string
  descriptiveVariable: Variable
}

export interface Variable {
  id: number
  name: string
  label: string
}

export const DATE_PATTERN = 'MM/dd/yy HH:mm'

export const YEAR_PATTERN = 'yyyy'

export const MONTH_PATTERN = 'MMM y'

export const FULL_MONTH_PATTERN = 'MMMM y'

export const WEEK_PATTERN = 'I R'

export const DAY_PATTERN = 'dd/MM/yyyy'

export const QUARTER_PATTERN = 'QQQ yyyy'

export class GapItem implements Gap {
  id: number

  contextModelVariantId: number

  dependentCharacteristic: Characteristic

  diversityDimensionCharacteristic: Characteristic

  referenceGroup: string

  groupOfInterest: string

  adjusted: number

  unadjusted: number

  unadjustedRegression: number

  timeOfImport: Date

  timeOfImportAsString: string

  timeOfImportYear: string

  timeOfImportDay: string

  timeOfImportWeek: string

  timeOfImportMonth: string

  timeOfImportQuarter: string

  constructor(gap: Gap) {
    this.id = gap.id
    this.contextModelVariantId = gap.contextModelVariantId
    this.dependentCharacteristic = gap.dependentCharacteristic
    this.diversityDimensionCharacteristic = gap.diversityDimensionCharacteristic
    this.referenceGroup = gap.referenceGroup
    this.groupOfInterest = gap.groupOfInterest
    this.adjusted = gap.adjusted
    this.unadjusted = gap.unadjusted
    this.unadjustedRegression = gap.unadjustedRegression
    this.timeOfImport = new Date(gap.timeOfImport)
    this.timeOfImportAsString = format(this.timeOfImport, DATE_PATTERN, {
      locale: defaultLocale,
    })
    this.timeOfImportYear = format(this.timeOfImport, YEAR_PATTERN, {
      locale: defaultLocale,
    })
    this.timeOfImportMonth = format(this.timeOfImport, MONTH_PATTERN, {
      locale: defaultLocale,
    })
    this.timeOfImportWeek = format(this.timeOfImport, WEEK_PATTERN, {
      locale: defaultLocale,
    })
    this.timeOfImportDay = format(this.timeOfImport, DAY_PATTERN, {
      locale: defaultLocale,
    })
    this.timeOfImportQuarter = format(this.timeOfImport, QUARTER_PATTERN, {
      locale: defaultLocale,
    })
  }
}

export interface Profile {
  preferredLanguage?: string | null
  isFirstTime?: boolean
  contactName?: string | null
  contactDesignation?: string | null
  contactEmail?: string | null
  contactNumber?: string | null
  appAccess?: string[] | null
}

export interface User {
  id: string
  email: string
  username: string
  isActive: boolean
  isSuperuser: boolean
  firstName?: string | null
  lastName?: string | null
  profile: Profile
}

export interface NewUser {
  email: string
  username: string
  isActive: boolean
  isSuperuser: boolean
  password: string
}

export interface AccountResponse {
  user: User
  access: string
  company: string
  refresh: string
}

export interface TokenResponse {
  access: string
  refresh: string
}

export interface ResponseMessage {
  status?: number
  severity?: AlertColor
  message?: string
}

export interface Run {
  id: number
  runAt: Date
}

export interface ConciseReport {
  id: number
  reportName: string
  reportModule: string
  reportType: string
  beginDate: string
  endDate: string
  locale: string
  createdAt: string
  updatedAt: string
  isEditable: boolean
  isNewPdfGenerated: boolean
  pdfGenerationStatus: string
}

export interface Report {
  id: number
  reportName: string
  reportModule: string
  reportType: string
  beginDate: string
  endDate: string
  locale: string
  createdAt: string
  updatedAt: string
  isEditable: boolean
  isNewPdfGenerated: boolean
  pdfGenerationStatus: string
  reportData: any
  reportTheme: any
}

export interface TabConfig {
  label: any
  value: string
  component: ReactNode | null
}

export interface ColumnConfig {
  field: string
  headerName: any
  minWidth: number
  disableColumnMenu: boolean
  sortable: boolean
  editable: boolean
  filterable: boolean
  renderCell: any
}

export interface RowConfig {
  id: number
  module: any
  name: string
  diversityDimension: any[]
  language: string
  dataFrom: string
  updatedAt: string
  status: string
}

export interface EtlFileMappings {
  primaryKey: string | null
  referenceFile: string | null
  foreignKey: string | null
}

export interface EtlStatus {
  id: number
  runAt: Date
  fullTimeEquivalent: number
  dateEffective: Date
  status: boolean
  step: number
  reason: string | null
}

export interface EtlFileCharacteristic {
  id: number
  empiricalConcept: {
    id: number
    name: string
    isDiversityDimension: boolean
    order: number
    run: number
  }
  originalCharacteristic?: number | null
  measurementUnit?: string | null
  name: string
  userColumnName: string | null
  userFileName: string | null
  userDatatype?: string | null
  requiredDatatype?: string | null
  scaleType?: string
  operation: string
  cut1?: string | null
  cut2?: string | null
  cut3?: string | null
  isShown: boolean
}

export interface EtlQuestion {
  id: number
  question: string | null
  row: string | null
  column: string | null
  answer: string | null
  run: number | null
}

export interface EtlValueMapping {
  id: number | null
  characteristic: number | null
  label: string | null
  originalValues: string[] | null
}

export interface EtlCharValueMapping {
  id: number
  name: string
  mappings: EtlValueMapping[]
}

export interface EtlFinalCheckError {
  id: number
  error: string | null
  row: string | null
  column: string | null
  comment: string | null
  report: boolean
}

export type Order = 'asc' | 'desc'

export interface OFATableHeadCell {
  id: keyof Omit<FsOFAValue, 'occupations'>
  label: string
  numeric?: boolean
  isNew?: boolean
}

export interface FsOFATableHeaderProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Omit<FsOFAValue, 'occupations'>,
  ) => void
  order: Order | null
  orderBy: string
  moduleConfig: ModuleConfig | undefined
  diversityDimenstionConfig: DiversityDimenstionConfig | undefined
}

export interface FsOFAValue {
  id: number
  run: number
  contextModelVariant: number
  ofaVariable: number
  ofaVariableLabel: string
  occupations?: string[]
  careerLevel: number | null
  newGap: string | null
  actualGap: string
  gapValueOverall: string
  totalEmployeesConsidered: string
  totalEmployeesConcerned: string
  currentCmAverage: string
  newCmAverage: string | null
  additionalCmAverage: string | null
  additionalCost: string | null
  costAsPercentageOfPayroll: string | null
  optionEfficiency: string
  isInitialOption: boolean
  isCurrentOption: boolean
}

export interface FsOFAOverall {
  overallGap: number
  currentOverallGap: number
  additionalCost: number
  totalMonthlyPayroll: number
  percentOfPayroll: number
  employeesConsidered: number
  percentEmployeesConsidered: number
}

export interface FsOFASliderR {
  rows: FsOFAValue[]
  id: number
  ofaVariable: number
  isItemSelected: boolean
  moduleConfig: ModuleConfig | undefined
  onChange: (ofaVariable: number, changedId: number) => void
  sliderChanges: Record<
    number | string,
    {
      curId: number
      changedId: number | null
    }
  >
}
